import { CompensationProfileModel, CrudAccess, EmployeeIdentityResponse, EmployeeModel, EmployeeStatusModel, JobTitleListModel, PayFrequency, TerminationReasonModel, WagePolicyModel } from "@herbjoy/apex-api-models";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { EventEmitter } from "../../../../common/components/event-emitter";
import { EmploymentStatusEnum } from "../../../../core/enum/employment-status";
import { EmploymentRecordEditContext, EmploymentRecordInputModel } from "./employment-record-edit-context.factory";
import templateUrl from "./employment-record-editor.component.html";

interface IcdOfficeSelect {
    Id: number;
    Text: string;
}

export const EmploymentRecordEditorComponent = {
    templateUrl,
    bindings: {
        /** The employment record being edited. */
        employmentRecord: "<",
        /** The identity of the app user. */
        identity: "<",
        /** Permissions regarding CRUD access to employment record. */
        roleAccess: "<",
        /** The edit context providing the list of options and methods for validating the record. */
        context: "<",
        /** Delegate logic to parent component for showing minimum wage requirements. */
        showMinimumWageRequirements: "&onShowMinimumWageRequirements"
    },
    controller: class EmploymentRecordEditor {
        //@Input
        employmentRecord: EmploymentRecordInputModel;
        //@Input
        identity: EmployeeIdentityResponse;
        //@Input
        roleAccess: CrudAccess;
        //@Input
        context: EmploymentRecordEditContext;
        //@Output
        showMinimumWageRequirements: EventEmitter<EmploymentRecordInputModel>;

        employee: EmployeeModel;
        pristineRecord: EmploymentRecordInputModel;
        employeeID: number;
        icdOffices: IcdOfficeSelect[];
        employmentStatuses: EmployeeStatusModel[];
        terminationReasons: TerminationReasonModel[];
        payFrequencies: PayFrequency[];
        jobTitles: JobTitleListModel[];
        compensationProfiles: CompensationProfileModel[];
        wagePolicies: WagePolicyModel[];
        oneYearAgo: Date;
        isReadOnly: boolean;
        employmentStatusChange: "rehire" | "termination" | null;
        $onInit() {
            this.employee = this.context.employee;
            this.icdOffices = this.context.icdOffices.map(office =>
                this._mapIcdOfficeSelectList(office, this.context.icdOffices.length > 1));
            this.employmentStatuses = this.context.employmentStatuses;
            this.terminationReasons = this.context.terminationReasons;
            this.payFrequencies = this.context.payFrequencies;
            this.jobTitles = this.context.jobTitles;
            this.compensationProfiles = this.context.compensationProfiles;

            this.oneYearAgo = moment().add(-1, "year").toDate();
            this.employeeID = this.employee.Id;
            const syncPeriodEnd = moment(this.employmentRecord.TerminationDate == null ? this.employmentRecord.HireDate : this.employmentRecord.TerminationDate).add(3, "weeks");
            this.isReadOnly = this.identity.Roles.includes("Owner") && this.employee.MeridianEmployeeID != null && moment().isBefore(syncPeriodEnd, "day");
        }

        $onChanges(changesObj) {
            if (changesObj.employmentRecord && this.employmentRecord) {
                this.pristineRecord = cloneDeep(this.employmentRecord);
            }
        }

        onChangeEmploymentStatus() {
            this.employmentRecord.EmployeeStatusID = this.employmentRecord.EmployeeStatus.EmployeeStatusID;
            if (this.pristineRecord.EmployeeStatusID === EmploymentStatusEnum.Terminated
                && this.employmentRecord.EmployeeStatusID === EmploymentStatusEnum.Active) {
                this.employmentStatusChange = "rehire";
                this.employmentRecord.HireDate = null;
                this.employmentRecord.TerminationDate = null;
                this.employmentRecord.TerminationReasonID = null;
            } else if (this.pristineRecord.EmployeeStatusID !== EmploymentStatusEnum.Terminated
                && this.employmentRecord.EmployeeStatusID === EmploymentStatusEnum.Terminated) {
                this.employmentStatusChange = "termination";
                this.employmentRecord.TerminationDate = null;
                this.employmentRecord.TerminationReasonID = null;
            } else if (this.pristineRecord.EmployeeStatusID === this.employmentRecord.EmployeeStatusID){
                // Revert to original hire and termination date if changing back to the original status.
                this.employmentStatusChange = null;
                this.employmentRecord.HireDate = this.pristineRecord.HireDate;
                this.employmentRecord.TerminationDate = this.pristineRecord.TerminationDate;
                this.employmentRecord.TerminationReasonID = this.pristineRecord.TerminationReasonID;
            }
        }

        minHireDate(office) {
            if (office.EmployeeID && this.employmentStatusChange !== "rehire") {
                // No limits if the employee already exists.
                return new Date(1900, 0, 1);
            }

            // Hire dates cannot be set more than a year before the current date.
            return this.oneYearAgo;
        }

        changeTerminationReason() {
            this.context.changeTerminationReason(this.employmentRecord);
        }

        changeEmploymentRecordIcdOffice() {
            this.context.changeEmploymentRecordIcdOffice(this.employmentRecord);
            if (this.employmentRecord.icdOfficeEmployeeForm) {
                this.employmentRecord.icdOfficeEmployeeForm.$setValidity("at-least-one-role",
                    this.anyRolesAssigned(), undefined);
            }
        }

        changePayFrequency() {
            this.context.changePayFrequency(this.employmentRecord);
        }

        changeJobTitle() {
            this.context.changeJobTitle(this.employmentRecord);
        }

        changeRole() {
            this.context.changeRole(this.employmentRecord);
            if (this.employmentRecord.icdOfficeEmployeeForm) {
                this.employmentRecord.icdOfficeEmployeeForm.$setValidity("at-least-one-role",
                    this.anyRolesAssigned(), undefined);
            }
        }

        changeCompensationProfile() {
            this.context.changeCompensationProfile(this.employmentRecord);
        }

        validateMinimumWage() {
            this.context.validateMinimumWage(this.employmentRecord);
        }

        anyRolesAssigned() {
            return this.context.anyRolesAssigned(this.employmentRecord);
        }

        _mapIcdOfficeSelectList(office, withOwner) {
            return {
                Id: office.Id,
                Text: withOwner
                    ? `${office.OwnerName} - ${office.IcdCorporationName}, ${office.OfficeName}`
                    : `${office.IcdCorporationName} - ${office.OfficeName}`
            };
        }
    }
}
